import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Flag,
  GitCompareArrows,
  LoaderCircle,
  MapPin,
  Shield,
  Users,
} from "lucide-react";

import Seo from "@/components/Seo";
import RiderFlag from "@/components/RiderFlag";
import TeamLogo from "@/components/TeamLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { getConfiguredSeason } from "@/lib/season";
import { getNativePlatform } from "@/lib/runtime";
import { useOpenOfficialMatch } from "@/hooks/useOpenOfficialMatch";
import { useOfficialMatches } from "@/queries/matches";
import { useStandings } from "@/queries/standings";
import { useTeam, useTeamRiders, useTeams } from "@/queries/teams";
import { formatSvDate, formatSvTime, getSvYear, toDate } from "@/utils/date";

import NativeSeriesPage from "@/components/native/NativeSeriesPage";

function normalizeTeamName(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatEntryAvg(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(3) : "-";
}

function formatMatchDayMonth(value) {
  return formatSvDate(value, {
    day: "numeric",
    month: "short",
  });
}

function formatPointsDiff(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "–";
  if (numeric > 0) return `+${numeric}`;
  return String(numeric);
}

function getCompactTeamName(value) {
  const normalized = normalizeTeamName(value);
  if (normalized === "rospiggarna hallstavik") return "Rospiggarna";
  if (normalized === "piraterna motala") return "Piraterna";
  return String(value || "");
}

function getMatchTimestamp(match) {
  return toDate(match?.date)?.getTime() || 0;
}

function isMatchFinished(match) {
  return Number.isFinite(Number(match?.home_score)) && Number.isFinite(Number(match?.away_score));
}

function isMatchInSeason(match, season) {
  const matchSeason = Number(match?.season);
  if (Number.isFinite(matchSeason)) return matchSeason === Number(season);
  return getSvYear(match?.date) === Number(season);
}

function matchIncludesTeam(match, team) {
  if (!match || !team) return false;

  const teamId = String(team.id || "");
  const teamName = normalizeTeamName(team.name);
  const values = [
    match.home_team_id,
    match.away_team_id,
    match.home_team,
    match.away_team,
    match.home_team_name_canonical,
    match.away_team_name_canonical,
  ]
    .filter(Boolean)
    .map(String);

  return values.includes(teamId) || values.some((value) => normalizeTeamName(value) === teamName);
}

function isTeamHome(match, team) {
  if (!match || !team) return false;
  return (
    String(match.home_team_id || "") === String(team.id || "") ||
    normalizeTeamName(match.home_team) === normalizeTeamName(team.name) ||
    normalizeTeamName(match.home_team_name_canonical) === normalizeTeamName(team.name)
  );
}

function getOpponentName(match, team) {
  if (!match || !team) return "Motståndare saknas";
  return getCompactTeamName(
    isTeamHome(match, team) ? match.away_team || "Motståndare saknas" : match.home_team || "Motståndare saknas"
  );
}

function getHomeAwayLabel(match, team) {
  return isTeamHome(match, team) ? "Hemma" : "Borta";
}

function getTeamScore(match, team) {
  if (!match || !team) return null;
  return isTeamHome(match, team) ? Number(match.home_score) : Number(match.away_score);
}

function getOpponentScore(match, team) {
  if (!match || !team) return null;
  return isTeamHome(match, team) ? Number(match.away_score) : Number(match.home_score);
}

function getMatchOutcome(match, team) {
  const teamScore = getTeamScore(match, team);
  const opponentScore = getOpponentScore(match, team);
  if (!Number.isFinite(teamScore) || !Number.isFinite(opponentScore)) return null;
  if (teamScore > opponentScore) return "V";
  if (teamScore < opponentScore) return "F";
  return "O";
}

function buildTeamMatchSummary(team, matches = []) {
  const teamMatches = matches
    .filter((match) => matchIncludesTeam(match, team))
    .sort((a, b) => getMatchTimestamp(b) - getMatchTimestamp(a));

  const finished = teamMatches.filter(isMatchFinished);
  const upcoming = teamMatches
    .filter((match) => !isMatchFinished(match))
    .sort((a, b) => getMatchTimestamp(a) - getMatchTimestamp(b));

  const wins = finished.filter((match) => getMatchOutcome(match, team) === "V").length;
  const losses = finished.filter((match) => getMatchOutcome(match, team) === "F").length;
  const scored = finished.reduce((sum, match) => sum + (getTeamScore(match, team) || 0), 0);
  const conceded = finished.reduce((sum, match) => sum + (getOpponentScore(match, team) || 0), 0);

  return {
    finished,
    upcoming,
    recentMatches: finished,
    nextMatch: upcoming[0] || null,
    wins,
    losses,
    scored,
    conceded,
    form: finished.slice(0, 5).map((match) => getMatchOutcome(match, team)).filter(Boolean),
  };
}

function TeamStatInline({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-background/60 px-3 py-3">
      <div className="flex items-center gap-2 text-muted-foreground">
        {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
        <span className="text-[10px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className="mt-2 text-base font-semibold text-foreground">{value}</div>
    </div>
  );
}

function TeamPointsDiffValue({ scored, conceded, diff }) {
  const diffClass =
    diff > 0
      ? "text-emerald-600 dark:text-emerald-300"
      : diff < 0
        ? "text-rose-600 dark:text-rose-300"
        : "text-foreground";

  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <span>{scored}</span>
      <span className="text-muted-foreground">/</span>
      <span>{conceded}</span>
      <span className={diffClass}>({diff > 0 ? `+${diff}` : diff})</span>
    </div>
  );
}

function HorizontalRail({ children, railRef = null }) {
  const items = React.Children.toArray(children);

  return (
    <div
      ref={railRef}
      className="recent-results-rail -mx-4 flex items-start snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pt-0.5 [scroll-padding-left:1rem] [scroll-padding-right:1rem] sm:mx-0 sm:[scroll-padding-left:0.25rem] sm:[scroll-padding-right:0.25rem]"
    >
        {items.map((child, index) => (
          <article
            key={index}
            className={`min-w-[172px] shrink-0 snap-start [scroll-snap-stop:always] sm:min-w-[196px] lg:min-w-[208px] ${
              index === 0 ? "ml-4 sm:ml-1" : ""
            } ${
              index === items.length - 1 ? "mr-4 sm:mr-1" : ""
            }`}
          >
            {child}
          </article>
        ))}
    </div>
  );
}

function TeamRecentMatchCard({ match, team }) {
  const opponent = getOpponentName(match, team);
  const outcome = getMatchOutcome(match, team);
  const homeScore = Number(match?.home_score);
  const awayScore = Number(match?.away_score);
  const currentTeamContext = isTeamHome(match, team) ? "hemmamatch" : "bortamatch";

  return (
    <Card className="h-full border-border bg-card">
      <CardContent className="p-1.5 sm:p-2">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {formatSvDate(match.date, { day: "numeric", month: "short" })}
          </div>
          {outcome ? (
            <Badge
              variant="outline"
              className={
                outcome === "V"
                  ? "h-5 border-emerald-500/40 px-1.5 text-[10px] text-emerald-600 dark:text-emerald-300"
                  : "h-5 border-rose-500/40 px-1.5 text-[10px] text-rose-600 dark:text-rose-300"
              }
            >
              {outcome}
            </Badge>
          ) : null}
        </div>

        <div className="flex min-h-[34px] items-center justify-center">
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold tabular-nums text-foreground sm:text-2xl">
              {Number.isFinite(homeScore) ? homeScore : "–"}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground">-</span>
            <span className="text-xl font-semibold tabular-nums text-foreground sm:text-2xl">
              {Number.isFinite(awayScore) ? awayScore : "–"}
            </span>
          </div>
        </div>

        <div className="mt-1 flex items-center gap-1.5">
          <TeamLogo teamName={opponent} size={18} className="h-4.5 w-4.5 shrink-0 object-contain" />
          <div className="min-w-0">
            <div className="truncate text-[11px] font-semibold text-foreground sm:text-xs">{opponent}</div>
            <div className="truncate text-[10px] text-muted-foreground">{currentTeamContext}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TeamUpcomingMatchCard({ match, team }) {
  const opponent = getOpponentName(match, team);
  const currentTeamContext = isTeamHome(match, team) ? "hemmamatch" : "bortamatch";

  return (
    <Card className="h-full border-border bg-card">
        <CardContent className="p-1.5 sm:p-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              kommande
            </div>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="flex min-h-[34px] flex-col items-center justify-center text-center">
            <div className="text-xs font-semibold text-foreground sm:text-sm">{formatMatchDayMonth(match.date)}</div>
            <div className="text-[11px] text-muted-foreground">
              {formatSvTime(match.date, { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>

          <div className="mt-1 flex items-center gap-1.5">
          <TeamLogo teamName={opponent} size={18} className="h-4.5 w-4.5 shrink-0 object-contain" />
          <div className="min-w-0">
            <div className="truncate text-[11px] font-semibold text-foreground sm:text-xs">{opponent}</div>
            <div className="truncate text-[10px] text-muted-foreground">{currentTeamContext}</div>
          </div>
        </div>
        </CardContent>
      </Card>
  );
}

function TeamListLogoName({ teamName, size = 34, className = "" }) {
  return (
    <TeamLogo
      teamName={getCompactTeamName(teamName)}
      size={size}
      className={className}
    />
  );
}

function RiderList({ riders = [], emptyText }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {riders.length ? (
        riders.map((rider) => (
          <div key={rider.id} className="rounded-xl border border-border/70 bg-background/70 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">{rider.name}</div>
                <div className="mt-1 flex items-center justify-start">
                  <RiderFlag countryCode={rider.country_code} />
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Ingångssnitt</div>
                <div className="font-semibold tabular-nums text-foreground">{formatEntryAvg(rider.entryAvg)}</div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
          {emptyText}
        </div>
      )}
    </div>
  );
}

function TeamSection({ title, description, children, plain = false, headerActions = null }) {
  if (plain) {
    return (
      <section className="min-w-0 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
          </div>
          {headerActions ? <div className="hidden shrink-0 lg:flex lg:items-center lg:gap-2">{headerActions}</div> : null}
        </div>
        <div className="min-w-0">{children}</div>
      </section>
    );
  }

  return (
    <Card className="min-w-0 overflow-hidden border-border/70 bg-card/90">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="min-w-0 overflow-hidden">{children}</CardContent>
    </Card>
  );
}

function TeamDetailSkeleton({ season }) {
  return (
    <section className="min-w-0 overflow-x-hidden space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-8 w-52 max-w-full" />
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>

      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
        <CardContent className="grid gap-5 p-5 sm:grid-cols-[auto,1fr]">
          <Skeleton className="h-24 w-24 rounded-3xl" />
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-10 rounded-lg" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function TeamGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} className="border-border/70 bg-card/90">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-2xl" />
                <div className="min-w-0 space-y-2">
                  <Skeleton className="h-5 w-28 max-w-full" />
                  <Skeleton className="h-4 w-20 max-w-full" />
                </div>
              </div>
              <Skeleton className="h-6 w-10 rounded-full" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
            </div>
            <Skeleton className="h-10 w-full rounded-md" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SeriesOverviewSkeleton() {
  return (
    <section>
      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/10">
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 rounded-full" />
          <Skeleton className="mt-3 h-9 w-56 rounded-lg" />
          <Skeleton className="mt-3 h-4 w-full rounded-full" />
          <Skeleton className="mt-2 h-4 w-4/5 rounded-full" />
          <div className="mt-5 flex gap-2">
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function TeamDetail({ team, standing, roster, season, matchSummary }) {
  const RECENT_MATCHES_BATCH_SIZE = 6;
  const allRiders = [...(roster?.mains || []), ...(roster?.reserves || [])].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "sv")
  );
  const rosterSectionRef = React.useRef(null);
  const scoredPoints = Number.isFinite(Number(standing?.points_for))
    ? Number(standing.points_for)
    : matchSummary.scored;
  const concededPoints = Number.isFinite(Number(standing?.points_against))
    ? Number(standing.points_against)
    : matchSummary.conceded;
  const pointsDiff = scoredPoints - concededPoints;
  const recentMatchesScrollerRef = React.useRef(null);
  const recentMatchesLoaderRef = React.useRef(null);
  const recentMatchesLoadTimerRef = React.useRef(null);
  const [recentMatchesVisibleCount, setRecentMatchesVisibleCount] = React.useState(RECENT_MATCHES_BATCH_SIZE);
  const [isLoadingMoreRecentMatches, setIsLoadingMoreRecentMatches] = React.useState(false);
  const [canScrollRecentPrev, setCanScrollRecentPrev] = React.useState(false);
  const [canScrollRecentNext, setCanScrollRecentNext] = React.useState(false);
  const displayedRecentMatches = React.useMemo(
    () => matchSummary.recentMatches.slice(0, recentMatchesVisibleCount),
    [matchSummary.recentMatches, recentMatchesVisibleCount]
  );
  const hasMoreRecentMatches = recentMatchesVisibleCount < matchSummary.recentMatches.length;

  const updateRecentScrollState = React.useCallback(() => {
    const container = recentMatchesScrollerRef.current;
    if (!container) {
      setCanScrollRecentPrev(false);
      setCanScrollRecentNext(false);
      return;
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    setCanScrollRecentPrev(container.scrollLeft > 8);
    setCanScrollRecentNext(container.scrollLeft < maxScrollLeft - 8);
  }, []);

  const scrollRecentResultsByPage = React.useCallback((direction) => {
    const container = recentMatchesScrollerRef.current;
    if (!container) return;

    const amount = Math.max(container.clientWidth * 0.9, 220);
    container.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  }, []);

  const loadMoreRecentMatches = React.useCallback(() => {
    if (isLoadingMoreRecentMatches || !hasMoreRecentMatches) return;

    setIsLoadingMoreRecentMatches(true);
    recentMatchesLoadTimerRef.current = window.setTimeout(() => {
      setRecentMatchesVisibleCount((current) =>
        Math.min(current + RECENT_MATCHES_BATCH_SIZE, matchSummary.recentMatches.length)
      );
      setIsLoadingMoreRecentMatches(false);
      recentMatchesLoadTimerRef.current = null;
    }, 220);
  }, [hasMoreRecentMatches, isLoadingMoreRecentMatches, matchSummary.recentMatches.length]);

  const handleScrollToRoster = React.useCallback(() => {
    const rosterSection = rosterSectionRef.current;
    if (!rosterSection) return;

    const top = rosterSection.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  }, []);

  React.useEffect(() => {
    setRecentMatchesVisibleCount(RECENT_MATCHES_BATCH_SIZE);
    setIsLoadingMoreRecentMatches(false);
    if (recentMatchesLoadTimerRef.current) {
      window.clearTimeout(recentMatchesLoadTimerRef.current);
      recentMatchesLoadTimerRef.current = null;
    }
  }, [matchSummary.recentMatches.length]);

  React.useEffect(() => {
    const container = recentMatchesScrollerRef.current;
    if (!container || isLoadingMoreRecentMatches || !hasMoreRecentMatches) return;

    if (container.scrollWidth <= container.clientWidth + 24) {
      loadMoreRecentMatches();
    }
  }, [
    displayedRecentMatches.length,
    hasMoreRecentMatches,
    isLoadingMoreRecentMatches,
    loadMoreRecentMatches,
  ]);

  React.useEffect(() => {
    const root = recentMatchesScrollerRef.current;
    const target = recentMatchesLoaderRef.current;
    if (!root || !target || !hasMoreRecentMatches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          loadMoreRecentMatches();
        }
      },
      {
        root,
        threshold: 0.85,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [displayedRecentMatches.length, hasMoreRecentMatches, loadMoreRecentMatches]);

  React.useEffect(() => {
    updateRecentScrollState();

    const container = recentMatchesScrollerRef.current;
    if (!container) return undefined;

    const handleScroll = () => updateRecentScrollState();
    container.addEventListener("scroll", handleScroll, { passive: true });

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleScroll);
    }

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleScroll);
      }
    };
  }, [displayedRecentMatches.length, updateRecentScrollState]);

  React.useEffect(() => {
    return () => {
      if (recentMatchesLoadTimerRef.current) {
        window.clearTimeout(recentMatchesLoadTimerRef.current);
        recentMatchesLoadTimerRef.current = null;
      }
    };
  }, []);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Lagsida</div>
          <h2 className="mt-1 text-2xl font-bold text-foreground">{team.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Lagprofil och säsongsdata för {season}.</p>
        </div>
        <Button variant="link" asChild>
          <Link to="/serie">
            <ArrowLeft className="h-4 w-4" />
            Tillbaka
          </Link>
        </Button>
      </div>

      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
        <CardContent className="grid gap-5 p-5 lg:grid-cols-[auto,1fr]">
          <div className="flex justify-center lg:justify-start">
            <div className="flex h-24 w-24 items-center justify-center rounded-3xl border border-border/70 bg-background/80 shadow-sm">
              <TeamListLogoName teamName={team.name} size={72} className="h-16 w-16 object-contain" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {standing?.position ? <Badge>#{standing.position} i tabellen</Badge> : null}
              {standing?.points != null ? <Badge variant="outline">{standing.points} poäng</Badge> : null}
              <Badge variant="outline">Säsong {season}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 xl:grid-cols-5">
              <TeamStatInline icon={MapPin} label="Ort" value={team.city || "Saknas"} />
              <TeamStatInline icon={Shield} label="Arena" value={team.arena || "Saknas"} />
              <TeamStatInline
                icon={CalendarDays}
                label="Matcher"
                value={standing?.played ?? matchSummary.finished.length ?? "–"}
              />
              <TeamStatInline
                icon={GitCompareArrows}
                label="Poängskillnad"
                value={<TeamPointsDiffValue scored={scoredPoints} conceded={concededPoints} diff={pointsDiff} />}
              />
              <button type="button" onClick={handleScrollToRoster} className="text-left">
                <TeamStatInline icon={Users} label="Trupp" value={`${allRiders.length}`} />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid min-w-0 gap-4 xl:grid-cols-2">
        <TeamSection plain title="Nästa match" description={`${team.name}s kommande möte`}>
          {matchSummary.nextMatch ? (
            <HorizontalRail>
              <TeamUpcomingMatchCard match={matchSummary.nextMatch} team={team} />
            </HorizontalRail>
          ) : (
            <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
              Ingen kommande match hittad för laget just nu.
            </div>
          )}
        </TeamSection>

        <TeamSection
          plain
          title="Senaste matcher"
          description={`${team.name}s senaste avslutade matcher`}
          headerActions={
            <>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => scrollRecentResultsByPage("prev")}
                disabled={!canScrollRecentPrev}
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">Föregående matcher</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => scrollRecentResultsByPage("next")}
                disabled={!canScrollRecentNext}
              >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">Nästa matcher</span>
              </Button>
            </>
          }
        >
          {matchSummary.recentMatches.length ? (
            <div className="space-y-3">
              <HorizontalRail railRef={recentMatchesScrollerRef}>
                {displayedRecentMatches.map((match) => (
                  <TeamRecentMatchCard
                    key={match.id || `${match.home_team}-${match.away_team}-${match.date}`}
                    match={match}
                    team={team}
                  />
                ))}
                {hasMoreRecentMatches ? (
                  <div
                    key="team-recent-matches-loader"
                    ref={recentMatchesLoaderRef}
                    className="flex min-h-full min-w-[72px] items-center justify-center"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border/80 bg-card/80 shadow-sm">
                      <LoaderCircle
                        className={`h-5 w-5 text-muted-foreground ${isLoadingMoreRecentMatches ? "animate-spin" : ""}`}
                      />
                    </div>
                  </div>
                ) : null}
              </HorizontalRail>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
              Det finns inga färdiga matcher att visa ännu.
            </div>
          )}
        </TeamSection>
      </div>

      <div ref={rosterSectionRef}>
        <TeamSection title={`Trupp ${season}`}>
          <RiderList riders={allRiders} emptyText="Inga förare registrerade ännu för detta lag." />
        </TeamSection>
      </div>
    </section>
  );
}

export default function SeriesPage() {
  const { teamId } = useParams();
  const season = getConfiguredSeason();
  const useNativeV3 = getNativePlatform() === "android";
  const openOfficialMatch = useOpenOfficialMatch({ enabled: useNativeV3 });

  const { data: teams = [], isLoading: teamsLoading, isError: teamsError } = useTeams();
  const { data: teamFromApi, isLoading: teamLoading } = useTeam(teamId, { enabled: Boolean(teamId) });
  const {
    data: roster,
    isLoading: rosterLoading,
    isError: rosterError,
  } = useTeamRiders(teamId, {
    season,
    active: true,
    enabled: Boolean(teamId),
  });

  const { data: standingsData, isLoading: standingsLoading } = useStandings(season, {
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: officialMatches = [], isLoading: officialMatchesLoading } = useOfficialMatches({
    staleTime: 2 * 60_000,
    gcTime: 15 * 60_000,
    refetchOnWindowFocus: false,
  });

  const seasonMatches = React.useMemo(
    () => officialMatches.filter((match) => isMatchInSeason(match, season)),
    [officialMatches, season]
  );

  const standingsRows = Array.isArray(standingsData?.rows) ? standingsData.rows : [];

  const standingsByTeamId = React.useMemo(
    () => new Map(standingsRows.map((row) => [String(row.team_id || ""), row])),
    [standingsRows]
  );

  const standingsByName = React.useMemo(
    () => new Map(standingsRows.map((row) => [normalizeTeamName(row.team), row])),
    [standingsRows]
  );

  const teamsWithStanding = React.useMemo(() => {
    return [...teams]
      .map((team) => {
        const standing =
          standingsByTeamId.get(String(team.id || "")) ||
          standingsByName.get(normalizeTeamName(team.name)) ||
          null;
        return { ...team, standing };
      })
      .sort((a, b) => {
        const posA = a.standing?.position ?? Number.POSITIVE_INFINITY;
        const posB = b.standing?.position ?? Number.POSITIVE_INFINITY;
        if (posA !== posB) return posA - posB;
        return String(a.name || "").localeCompare(String(b.name || ""), "sv");
      });
  }, [standingsByName, standingsByTeamId, teams]);

  const selectedTeam =
    teamsWithStanding.find((team) => String(team.id) === String(teamId || "")) ||
    (teamFromApi
      ? {
          ...teamFromApi,
          standing:
            standingsByTeamId.get(String(teamFromApi.id || "")) ||
            standingsByName.get(normalizeTeamName(teamFromApi.name)) ||
            null,
        }
      : null);

  const selectedStanding = selectedTeam?.standing || null;
  const selectedTeamMatchSummary = React.useMemo(
    () => buildTeamMatchSummary(selectedTeam, seasonMatches),
    [selectedTeam, seasonMatches]
  );

  const seoTitle = selectedTeam ? `${selectedTeam.name} - Serie` : "Serieöversikt";
  const seoDescription = selectedTeam
    ? `Se laginformation, trupp och tabelläge för ${selectedTeam.name} i Lapstrs serieöversikt för säsong ${season}.`
    : `Upptäck lag, trupper och serieöversikt för speedwaysäsongen ${season} i Lapstr.`;
  const canonicalPath = selectedTeam ? `/serie/lag/${selectedTeam.id}` : "/serie";
  const showTeamDetailSkeleton =
    Boolean(teamId) &&
    !rosterError &&
    (teamLoading || rosterLoading || standingsLoading || officialMatchesLoading || (teamsLoading && !selectedTeam));

  if (useNativeV3) {
    return (
      <>
        <Seo title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
        <NativeSeriesPage
            season={season}
            teamId={teamId}
            teams={teamsWithStanding}
            teamsLoading={teamsLoading || standingsLoading}
            teamsError={teamsError}
            selectedTeam={selectedTeam}
            selectedStanding={selectedStanding}
            roster={roster}
            rosterError={rosterError}
            matchSummary={selectedTeamMatchSummary}
            detailLoading={showTeamDetailSkeleton}
            onOpenMatch={openOfficialMatch}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <Seo title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />

      {!selectedTeam ? (
        teamsLoading || standingsLoading ? (
          <SeriesOverviewSkeleton />
        ) : (
        <section>
  <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/10">
    <CardContent className="p-6">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
        <Flag className="h-4 w-4" />
        Serien
      </div>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-foreground">Serieöversikt</h1>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
        Följ säsong {season}, jämför lagen i tabellen och gå vidare in till varje lags trupp och matchläge.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Badge>Säsong {season}</Badge>
      </div>
    </CardContent>
  </Card>
</section>
        )
      ) : null}

      {showTeamDetailSkeleton ? (
        <TeamDetailSkeleton season={season} />
      ) : selectedTeam ? (
        <TeamDetail
          team={selectedTeam}
          standing={selectedStanding}
          roster={roster}
          season={season}
          matchSummary={selectedTeamMatchSummary}
        />
      ) : null}

      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Alla lag i serien</h2>
          </div>
        </div>

        <Separator />

        {teamsError ? (
          <Card className="border-destructive/30">
            <CardContent className="p-5 text-sm text-destructive">Kunde inte ladda lagen just nu.</CardContent>
          </Card>
        ) : teamsLoading || standingsLoading ? (
          <TeamGridSkeleton />
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {teamsWithStanding.map((team) => (
              <Card
                key={team.id}
                className={`border-border/70 bg-card/90 transition-all ${
                  String(team.id) === String(teamId || "") ? "border-primary/50 shadow-md" : "hover:border-primary/30 hover:shadow-sm"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-end">
                    {team.standing?.position ? <Badge>#{team.standing.position}</Badge> : null}
                  </div>

                  <div className="mt-2 flex flex-col items-center text-center">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-border/70 bg-background/80">
                      <TeamListLogoName teamName={team.name} size={40} className="h-10 w-10 object-contain" />
                    </div>
                    <div className="mt-3 text-base font-semibold leading-tight text-foreground">{team.name}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {team.city || team.arena || "Lagprofil"}
                    </div>
                  </div>
                  <Button
                    asChild
                    variant="outline"
                    className="mt-4 w-full justify-between rounded-xl border-primary/30 bg-primary/5 px-4 text-primary hover:bg-primary/10 hover:text-primary"
                  >
                    <Link to={`/serie/lag/${team.id}`}>
                      Visa lagsida
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {teamId && !selectedTeam && !teamLoading ? (
        <Card className="border-destructive/30">
          <CardContent className="p-5 text-sm text-destructive">Laget kunde inte hittas.</CardContent>
        </Card>
      ) : null}

      {teamId && rosterError ? (
        <Card className="border-destructive/30">
          <CardContent className="p-5 text-sm text-destructive">
            Kunde inte ladda lagets förare just nu.
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}


